import type { FloorRasterDecodedTile, FloorRasterEncodedTile } from './floorRasterTypes';

const DB_NAME = 'fab-planner-floor-raster';
const DB_VERSION = 1;
const TILE_STORE = 'tiles';
const MAX_MEMORY_BYTES = 512 * 1024 * 1024;
const DEFAULT_PERSISTENT_BYTES = 256 * 1024 * 1024;
const EVICTION_GRACE_PERIOD_MS = 5000;

type StoredTile = FloorRasterEncodedTile & {
  byteSize: number;
  lastAccess: number;
};

type MemoryEntry = {
  tile: FloorRasterDecodedTile;
  byteSize: number;
  lastAccess: number;
  refs: number;
};

const memory = new Map<string, MemoryEntry>();
let memoryBytes = 0;
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(TILE_STORE)) {
        const store = db.createObjectStore(TILE_STORE, { keyPath: 'key' });
        store.createIndex('lastAccess', 'lastAccess');
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return dbPromise;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T | null> {
  return new Promise((resolve) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
  });
}

async function persistentBudget(): Promise<number> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (estimate?.quota) {
      return Math.max(32 * 1024 * 1024, Math.min(DEFAULT_PERSISTENT_BYTES, estimate.quota * 0.1));
    }
  } catch {
    // Storage estimates are optional; the fixed cap remains safe.
  }
  return DEFAULT_PERSISTENT_BYTES;
}

async function evictPersistentTiles(db: IDBDatabase): Promise<void> {
  const budget = await persistentBudget();
  const transaction = db.transaction(TILE_STORE, 'readonly');
  const all = (await requestResult(
    transaction.objectStore(TILE_STORE).getAll()
  )) as StoredTile[] | null;
  if (!all) return;
  let total = all.reduce((sum, tile) => sum + tile.byteSize, 0);
  if (total <= budget) return;

  const oldest = [...all].sort((a, b) => a.lastAccess - b.lastAccess);
  const write = db.transaction(TILE_STORE, 'readwrite').objectStore(TILE_STORE);
  for (const tile of oldest) {
    if (total <= budget) break;
    write.delete(tile.key);
    total -= tile.byteSize;
  }
}

export async function getCachedFloorRasterTile(
  key: string
): Promise<FloorRasterEncodedTile | null> {
  const db = await openDb();
  if (!db) return null;
  const transaction = db.transaction(TILE_STORE, 'readonly');
  const store = transaction.objectStore(TILE_STORE);
  const stored = (await requestResult(store.get(key))) as StoredTile | null;
  if (!stored) return null;
  stored.lastAccess = Date.now();
  try {
    db.transaction(TILE_STORE, 'readwrite').objectStore(TILE_STORE).put(stored);
  } catch {
    // Recency is an eviction hint only; a readable tile remains a valid hit.
  }
  return {
    key: stored.key,
    level: stored.level,
    column: stored.column,
    row: stored.row,
    worldBounds: stored.worldBounds,
    pixelWidth: stored.pixelWidth,
    pixelHeight: stored.pixelHeight,
    pixelsPerWorldUnit: stored.pixelsPerWorldUnit,
    blob: stored.blob,
  };
}

export async function putCachedFloorRasterTile(tile: FloorRasterEncodedTile): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const stored: StoredTile = {
    ...tile,
    byteSize: tile.blob.size,
    lastAccess: Date.now(),
  };
  const transaction = db.transaction(TILE_STORE, 'readwrite');
  transaction.objectStore(TILE_STORE).put(stored);
  await new Promise<void>((resolve) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
  void evictPersistentTiles(db);
}

function evictMemoryTiles(): void {
  if (memoryBytes <= MAX_MEMORY_BYTES) return;
  const now = Date.now();
  // A bitmap released this frame may still be bound to a 3D texture. Closing it
  // immediately blanks the DXF while the camera is moving. Only bitmaps that
  // have been unreferenced for the full grace period are eligible.
  const candidates = [...memory.entries()]
    .filter(
      ([, entry]) =>
        entry.refs === 0 && now - entry.lastAccess >= EVICTION_GRACE_PERIOD_MS
    )
    .sort(([, a], [, b]) => a.lastAccess - b.lastAccess);
  for (const [key, entry] of candidates) {
    if (memoryBytes <= MAX_MEMORY_BYTES) break;
    try {
      if (entry.tile.image && typeof entry.tile.image.close === 'function') {
        entry.tile.image.close();
      }
    } catch {
      // Ignore if already closed or environment doesn't support close
    }
    memory.delete(key);
    memoryBytes -= entry.byteSize;
  }
}

export async function acquireDecodedFloorRasterTile(
  tile: FloorRasterEncodedTile
): Promise<FloorRasterDecodedTile> {
  const hit = memory.get(tile.key);
  if (hit) {
    hit.refs += 1;
    hit.lastAccess = Date.now();
    return hit.tile;
  }
  if (typeof createImageBitmap !== 'function') {
    throw new Error('ImageBitmap decoding is unavailable');
  }
  const image = await createImageBitmap(tile.blob);
  const decoded: FloorRasterDecodedTile = { ...tile, image };
  const byteSize = tile.pixelWidth * tile.pixelHeight * 4;
  memory.set(tile.key, {
    tile: decoded,
    byteSize,
    lastAccess: Date.now(),
    refs: 1,
  });
  memoryBytes += byteSize;
  evictMemoryTiles();
  return decoded;
}

export function releaseDecodedFloorRasterTile(key: string): void {
  const entry = memory.get(key);
  if (!entry) return;
  entry.refs = Math.max(0, entry.refs - 1);
  entry.lastAccess = Date.now();
  evictMemoryTiles();
}

export function clearFloorRasterMemoryCache(): void {
  for (const entry of memory.values()) {
    try {
      if (entry.tile.image && typeof entry.tile.image.close === 'function') {
        entry.tile.image.close();
      }
    } catch {
      // Ignore
    }
  }
  memory.clear();
  memoryBytes = 0;
}

export async function clearPersistentFloorRasterCache(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  const transaction = db.transaction(TILE_STORE, 'readwrite');
  transaction.objectStore(TILE_STORE).clear();
}

export const floorRasterCacheDiagnostics = {
  memoryBytes: () => memoryBytes,
  memoryEntries: () => memory.size,
  maxMemoryBytes: MAX_MEMORY_BYTES,
};

